/**
 * Guards against the search URL growing a bracket pair on every search.
 *
 * `append-query` serializes an array value under key `k` as `k[]=a&k[]=b`, but
 * react-router parses that back under the *literal* key `k[]` rather than as an
 * array under `k`. Anywhere we spread the current query to carry params forward
 * and then re-serialize, that retained key picks up another pair — `bounds`
 * becomes `bounds[]`, then `bounds[][]`, then `bounds[][][]` — and every earlier
 * generation is kept alongside the new one, so the URL grows without bound.
 *
 * It also silently defeats key deletion: code that clears stale params with
 * `delete restQuery.bounds` (CMS #22751) stops matching once the key is spelled
 * `bounds[]`, so the stale values it means to drop survive.
 *
 * Nothing reads these serialized-array keys back — `bounds` is written to the
 * URL for sharing but the search is rehydrated from `address`, so dropping them
 * loses nothing. Callers that still want the value pass it explicitly under the
 * unbracketed key.
 *
 * Every generation ends in `[]`, so this also cleans up a URL that was already
 * polluted before the fix landed.
 */
export const stripSerializedArrayKeys = (query = {}) =>
  Object.fromEntries(
    Object.entries(query).filter(([key]) => !key.endsWith('[]')),
  );
